import { z } from "zod";
const id = z.string().uuid();
const text = z.string().trim().min(1).max(1000);
export const ChecklistItemSchema = z.object({
  id: text,
  category: text,
  label: text,
  /** Пункт только для вагонов с ДГУ (раздел «Дизельная генераторная установка»). */
  requiresGenerator: z.boolean().optional(),
});
export const ChecklistSchema = z
  .array(ChecklistItemSchema)
  .min(1)
  .max(100)
  .refine(
    (a) => new Set(a.map((x) => x.id)).size === a.length,
    "Повторяется пункт",
  );
export const OnboardPhotoSchema = z.object({
  fileName: text,
  mimeType: z.enum(["image/jpeg", "image/png"]),
  contentBase64: z.string().max(350000),
});
export const OnboardAnswerSchema = z
  .object({
    itemId: text,
    status: z.enum(["ok", "warning", "defect", "not_checked"]),
    comment: z.string().max(2000).default(""),
    photo: OnboardPhotoSchema.optional(),
    createDefect: z.boolean().default(false),
    priority: z.enum(["low", "medium", "high", "critical"]).default("medium"),
  })
  .superRefine((a, c) => {
    if (
      (a.status === "warning" || a.status === "defect") &&
      (!a.comment.trim() || !a.photo)
    )
      c.addIssue({ code: "custom", message: "Добавьте комментарий и фото" });
    if (a.createDefect && !["warning", "defect"].includes(a.status))
      c.addIssue({
        code: "custom",
        message: "Дефект возможен только при проблеме",
      });
  });
export const SaveOnboardResultSchema = z.object({
  carriageId: id,
  requestKey: id,
  answers: z.array(OnboardAnswerSchema).min(1).max(100),
  /**
   * Время на телефоне бортмеханика (статистика БЭМ): когда открыл вагон, когда
   * отсканировал его QR (нет — вагон открыт нажатием на схему) и когда
   * завершил осмотр вагона. Необязательные: установленные сборки их не шлют, и
   * результат без них принимается как прежде. Неправдоподобное время сервер
   * не сохраняет, а сам результат принимает.
   */
  openedAt: z.string().datetime().optional(),
  scannedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
});
export const CreatePassengerTripSchema = z
  .object({
    trainId: id,
    trainNumber: text,
    departureStationId: id,
    arrivalStationId: id,
    plannedStart: z.string().datetime(),
    plannedEnd: z.string().datetime(),
    /**
     * Явка по графику (шаг 6). Опущена — сервер ставит её за
     * `OnboardSettings.reportLeadMinutes` до отправления. Часы бригады идут от
     * явки, поэтому задаёт её мастер, а не считает телефон.
     */
    reportAt: z.string().datetime().optional(),
    assignedMembershipId: id,
    /** Бригада рейса. Опущена — один БМ1, назначенный выше. */
    crew: z.lazy(() => TripCrewSchema).optional(),
    /**
     * Состав рейса: вагоны парка по порядку от головы поезда. Опущен — состав
     * берётся из шаблона поезда. Количество вагонов любое, но не пустое.
     */
    carriageIds: z
      .array(id)
      .min(1)
      .max(100)
      .refine((a) => new Set(a).size === a.length, "Вагон указан дважды")
      .optional(),
    intervalMinutes: z
      .number()
      .int()
      .min(1)
      .max(10080)
      .nullable()
      .default(null),
  })
  .refine(
    (x) => x.plannedEnd > x.plannedStart,
    "Конец рейса должен быть позже начала",
  );
export type CreatePassengerTrip = z.infer<typeof CreatePassengerTripSchema>;
/**
 * Вагон парка организации: заводится номером и типом. Постоянный QR-код ему
 * присваивает сервер при заведении и больше не меняет — наклейка висит на
 * вагоне навсегда. Формат номера не проверяется (текст, уникален в организации).
 */
export const UpsertRailCarriageSchema = z.object({
  id: id.optional(),
  carriageNumber: z.string().trim().min(1).max(50),
  carriageTypeId: id,
});
export type UpsertRailCarriage = z.infer<typeof UpsertRailCarriageSchema>;
/** Ввод вагона в парк и вывод из парка. */
export const SetRailCarriageStatusSchema = z.object({
  status: z.enum(["active", "inactive"]),
});
/**
 * Шаблон состава поезда: вагоны парка по порядку от головы. Без `id` заводится
 * новый поезд.
 */
export const SaveTrainCompositionSchema = z.object({
  id: id.optional(),
  number: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(255),
  carriageIds: z
    .array(id)
    .min(1)
    .max(100)
    .refine((a) => new Set(a).size === a.length, "Вагон указан дважды"),
});
export type SaveTrainComposition = z.infer<typeof SaveTrainCompositionSchema>;
/** Роль в бригаде рейса: БМ1, БМ2, … */
export const CrewRoleSchema = z.string().regex(/^БМ[1-9][0-9]?$/, "Роль в бригаде — БМ1, БМ2, …");
export const TripCrewSchema = z
  .array(z.object({ membershipId: id, role: CrewRoleSchema }))
  .min(1)
  .max(10)
  .refine((a) => new Set(a.map((x) => x.membershipId)).size === a.length, "Бортмеханик назначен дважды")
  .refine((a) => new Set(a.map((x) => x.role)).size === a.length, "Роль в бригаде повторяется")
  .refine((a) => a.some((x) => x.role === "БМ1"), "В бригаде нужен БМ1");
export const SetTripCrewSchema = z.object({ crew: TripCrewSchema });

// ─── График рейсов мастера (шаг 4) ──────────────────────────────────────

/**
 * Как повторять рейс. Рейсов много и каждый день, поэтому мастер заводит не
 * один рейс, а расписание на неделю вперёд.
 *
 * `once` — один рейс на названную дату. `daily` — каждый день. `alternate` —
 * через день, начиная с названной даты.
 */
export const TripRepeatSchema = z.enum(["once", "daily", "alternate"]);
export type TripRepeat = z.infer<typeof TripRepeatSchema>;

/**
 * Расписание рейса на неделю вперёд.
 *
 * Время берётся из `plannedStart` / `plannedEnd` первого рейса и переносится
 * на каждый следующий день без изменения: рейс, уходящий в 08:10, уходит в
 * 08:10 и завтра. Горизонт — `days` дней ОТ первой даты включительно, не
 * больше четырнадцати: расписание на год вперёд мастер не ведёт, а опечатка в
 * числе не должна заводить тысячу рейсов.
 */
export const PlanTripsSchema = CreatePassengerTripSchema.innerType()
  .extend({
    repeat: TripRepeatSchema.default("once"),
    days: z.number().int().min(1).max(14).default(7),
  })
  .refine((x) => x.plannedEnd > x.plannedStart, "Конец рейса должен быть позже начала");

/**
 * Правка заведённого рейса (по проверке на телефоне 08.10.2026).
 *
 * `scope` отвечает на вопрос, который у расписания возникает всегда: мастер
 * поправил время — это на один день или со следующими тоже? Ответ не
 * угадывается: `day` меняет только этот рейс, `forward` — его и все
 * последующие запланированные рейсы того же поезда. Прошедшие дни не
 * меняются ни при каком выборе: что было, то было.
 */
export const EditTripScopeSchema = z.enum(["day", "forward"]);
export type EditTripScope = z.infer<typeof EditTripScopeSchema>;

export const EditTripSchema = z
  .object({
    plannedStart: z.string().datetime(),
    plannedEnd: z.string().datetime(),
    departureStationId: id,
    arrivalStationId: id,
    assignedMembershipId: id,
    /** Состав вагонами парка по порядку. Опущен — состав рейса не меняется. */
    carriageIds: z.array(id).min(1).max(100).optional(),
    crew: z.lazy(() => TripCrewSchema).optional(),
    scope: EditTripScopeSchema.default("day"),
  })
  .refine((x) => x.plannedEnd > x.plannedStart, "Конец рейса должен быть позже начала");

/** Какие рейсы правка затронула. */
export interface OnboardEditResult {
  changed: { date: string; tripId: string; trainNumber: string }[];
}

/** Перенос недели: рейсы с бригадами копируются на следующие семь дней. */
export const CopyTripWeekSchema = z.object({
  /** Первый день переносимой недели, YYYY-MM-DD. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Дата в виде ГГГГ-ММ-ДД"),
  days: z.number().int().min(1).max(14).default(7),
});

/** Итог заведения расписания: что создано и что уже было. */
export interface OnboardPlanResult {
  created: { date: string; tripId: string; trainNumber: string }[];
  /**
   * Дни, где рейс этого поезда уже стоял. Повторное сохранение не плодит
   * дубли, и мастер видит не «готово», а что именно уже было.
   */
  skipped: { date: string; tripId: string; trainNumber: string }[];
}

/** Рейс в графике мастера — одна карточка дня. */
export interface OnboardScheduleTrip {
  id: string;
  /**
   * Поезд, станции и вагоны идентификаторами — чтобы карточку можно было
   * ОТКРЫТЬ НА ПРАВКУ той же формой, что заводит рейс. Названия ниже остаются:
   * карточка показывает их, а не ищет по справочнику.
   */
  trainId: string;
  departureStationId: string;
  arrivalStationId: string;
  carriageIds: string[];
  trainNumber: string;
  trainName: string | null;
  departure: string;
  arrival: string;
  plannedStart: string;
  plannedEnd: string;
  status: string;
  /** Вагонов в составе и их типы от головы — полоска состава. */
  carriages: number;
  strip: (string | null)[];
  crew: { membershipId: string; role: string; name: string }[];
  /** Роли обязательной бригады, которых в рейсе нет: «Нет БМ2». */
  missingRoles: string[];
  /**
   * Назначенные, у кого на дату рейса допуск не действует — например истёк уже
   * после назначения. Красная плашка на карточке рейса.
   */
  crewIssues: { membershipId: string; role: string; name: string; reasons: string[] }[];
}

/** Неделя графика одним ответом: дни с отметкой и рейсы каждого дня. */
export interface OnboardSchedule {
  from: string;
  days: {
    date: string;
    trips: number;
    /** Рейсы этого дня, где не хватает бригады: точка-индикатор дня. */
    incomplete: number;
  }[];
  trips: (OnboardScheduleTrip & { date: string })[];
}

/**
 * Допуск бортмеханика к рейсу на дату рейса: зелёный (`ok`), жёлтый (`warning`
 * — допуск истекает, назначить можно) или красный (`blocked` — назначить
 * нельзя, причина названа). Считается по обычным допускам работника.
 */
export type CrewAdmissionLevel = "ok" | "warning" | "blocked";
export interface CrewAdmission {
  level: CrewAdmissionLevel;
  /** Что именно не так, по-русски, по одной причине на строку. Пусто при `ok`. */
  reasons: string[];
  /** Через сколько суток кончается ближайший допуск; только при `warning`. */
  daysLeft: number | null;
  expiresAt: string | null;
}

/** Бортмеханик в выборе бригады — с допуском на дату рейса и пометкой, если он уже занят в этот день. */
export interface OnboardCrewCandidate {
  membershipId: string;
  name: string;
  employeeNumber: string | null;
  /** Рейс этого дня, в котором человек уже занят. Пусто — свободен. */
  busy: { tripId: string; trainNumber: string; role: string } | null;
  admission: CrewAdmission;
}

/** Обязательные роли бригады рейса: без второго бортмеханика рейс неполон. */
export const REQUIRED_CREW_ROLES = ["БМ1", "БМ2"] as const;
/** Тип вагона в справочнике организации. */
export const UpsertRailCarriageTypeSchema = z.object({
  id: id.optional(),
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(255),
  sortOrder: z.number().int().min(0).max(10000).default(0),
  hasGenerator: z.boolean().default(false),
  active: z.boolean().default(true),
});
/**
 * Чек-лист осмотра для конкретного вагона: раздел ДГУ — только у вагона, тип
 * которого отмечен «есть ДГУ». Вагон без сведений о типе (заведённый до
 * справочника) получает чек-лист без пунктов ДГУ.
 */
export function checklistForCarriage<T extends { requiresGenerator?: boolean }>(
  items: readonly T[],
  carriage: { hasGenerator?: boolean | null } | null | undefined,
): T[] {
  return items.filter((item) => !item.requiresGenerator || carriage?.hasGenerator === true);
}
export const PlanOnboardInspectionSchema = z.object({
  templateId: id,
  type: z.enum(["acceptance", "periodic", "handover"]),
  plannedAt: z.string().datetime(),
});
export const CreateRailRepairSchema = z.object({
  defectId: id,
  workTypeId: id.nullable().optional(),
  assignedMembershipId: id.nullable().optional(),
  plannedAt: z.string().datetime().nullable().optional(),
  comment: z.string().max(2000).optional(),
});
export const OnboardTransitionSchema = z.object({
  status: text,
  assignedMembershipId: id.optional(),
  workOrderId: id.optional(),
  permitDecision: z
    .enum(["requires_decision", "without_work_order", "work_order_required"])
    .optional(),
  operationAllowed: z.enum(["yes", "no", "requires_decision"]).optional(),
});
/**
 * Сдача рейса (шаг 3а).
 *
 * `requestKey` — ключ повтора, как у результата осмотра: связь в поезде рвётся,
 * и повторная отправка не должна сдавать рейс дважды.
 *
 * `unlockMethod` — чем бортмеханик подтвердил действие на телефоне. Перечень
 * тот же, которым подтверждаются подписи ключом устройства
 * (`DeviceUnlockMethodSchema` в core): биометрия или код устройства (ПИН,
 * графический ключ). Нового способа подтверждения не заводится.
 *
 * `deviceId` — ключ устройства, если подтверждение шло им. Необязателен:
 * устройство бывает не зарегистрировано, а сдать рейс в конце смены надо.
 * Сервер проверяет, что названный ключ принадлежит этому работнику в этой
 * организации и не отозван.
 */
export const HandoverTripSchema = z.object({
  requestKey: id,
  unlockMethod: z.enum(["BIOMETRIC", "DEVICE_CREDENTIAL"]),
  deviceId: id.optional(),
});
export type HandoverTrip = z.infer<typeof HandoverTripSchema>;

export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;
export type OnboardAnswer = z.infer<typeof OnboardAnswerSchema>;
export type SaveOnboardResult = z.infer<typeof SaveOnboardResultSchema>;
export interface OnboardCarriage {
  id: string;
  carriageNumber: string;
  type: string;
  qrValue: string;
  /** Место в составе от головы, с 1. У рейсов до шага 1 — нет. */
  position?: number | null;
  carriageTypeId?: string | null;
  typeCode?: string | null;
  typeName?: string | null;
  hasGenerator?: boolean | null;
}
/** Вагон парка: строка списка «Парк вагонов». */
export interface OnboardFleetCar {
  id: string;
  carriageNumber: string;
  carriageTypeId: string | null;
  /** Код типа (WG, WSb, WS, WL); у вагона без типа в справочнике — пусто. */
  typeCode: string | null;
  typeName: string | null;
  hasGenerator: boolean;
  /** Постоянный код наклейки. */
  qrValue: string;
  status: "active" | "inactive";
  /** Шаблоны поездов, в которые вагон входит. Пусто у действующего вагона — он в резерве. */
  trains: { id: string; number: string }[];
  /**
   * Рейс, в котором вагон стоит в запрошенный день (с учётом исключённого
   * рейса). Только когда день передан.
   */
  busy: { tripId: string; trainNumber: string } | null;
  /** Вагон был в рейсах или стоит в осмотрах/дефектах — удалить нельзя, только вывести из парка. */
  hasHistory: boolean;
}
export interface RailCarriageTypeDto {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  hasGenerator: boolean;
  active: boolean;
}
/** Итог осмотра вагона на экране «Мой рейс». */
export type CarriageCheckState = "ok" | "warning" | "defect" | "not_inspected";
export type OnboardStageKind = "acceptance" | "periodic" | "handover";
/** Экран «Мой рейс» одним ответом. */
export interface OnboardBoard {
  trip: {
    id: string;
    trainNumber: string;
    trainName: string | null;
    status: string;
    departure: string;
    arrival: string;
    plannedStart: string;
    plannedEnd: string;
    /** Явка по графику (шаг 6); у рейсов, заведённых раньше, пусто. */
    reportAt: string | null;
    actualStart: string | null;
    actualEnd: string | null;
  };
  crew: {
    membershipId: string;
    role: string;
    name: string;
    employeeNumber: string | null;
    isMe: boolean;
    /** Фактическая явка: нажал «Я на месте». */
    reportedAt: string | null;
  }[];
  carriages: {
    id: string;
    position: number;
    carriageNumber: string;
    typeCode: string | null;
    typeName: string;
    hasGenerator: boolean;
    state: CarriageCheckState;
    openDefects: number;
  }[];
  /** Обход, по которому посчитан итог вагонов: текущий, иначе последний завершённый. */
  stateInspectionId: string | null;
  stages: {
    id: string;
    kind: OnboardStageKind;
    number: number;
    title: string;
    status: string;
    plannedAt: string;
    startedAt: string | null;
    completedAt: string | null;
    progress: { done: number; total: number };
  }[];
  currentStageId: string | null;
}
export interface OnboardInspection {
  id: string;
  inspectionNumber: number;
  type: string;
  status: string;
  plannedAt: string;
  templateSnapshot: { name: string; version: number; items: ChecklistItem[] };
  results: { id: string; carriageId: string; answers: OnboardAnswer[] }[];
}
export interface OnboardDefect {
  id: string;
  /** Поля строки дефекта, которые сервер отдаёт в рейсе. */
  carriageId?: string;
  inspectionId?: string;
  resultId?: string;
  itemId?: string;
  category?: string;
  equipment?: string;
  createdByMembershipId?: string;
  createdAt?: string;
  description: string;
  priority: string;
  status: string;
  operationAllowed: string;
  assignedMembershipId: string | null;
  repairTasks: OnboardRepair[];
}
export interface OnboardRepair {
  permitDecision: string;
  id: string;
  status: string;
  workOrderId: string | null;
  assignedMembershipId: string | null;
}
export interface OnboardTrip {
  id: string;
  trainNumber: string;
  status: string;
  assignedMembershipId: string;
  plannedStart: string;
  plannedEnd: string;
  compositionSnapshot: OnboardCarriage[];
  stationSnapshot: { departure: string; arrival: string };
  inspections: OnboardInspection[];
  defects: OnboardDefect[];
  assignee?: { user: { name: string } };
  timeline?: {
    id: string;
    occurredAt: string;
    details: { action: string; status?: string };
  }[];
}
export interface OnboardCatalog {
  workOrders: { id: string; number: string; status: string }[];
  /** Поезда со своим составом: полоска вагонов нужна форме нового рейса. */
  trains: {
    id: string;
    number: string;
    name: string;
    carriages?: {
      id: string;
      carriageNumber: string;
      type: string;
      qrValue: string;
      position: number | null;
      carriageTypeId: string | null;
    }[];
  }[];
  stations: { id: string; name: string }[];
  memberships: { id: string; user: { name: string } }[];
  templates: {
    id: string;
    name: string;
    version: number;
    items: ChecklistItem[];
  }[];
  workTypes: { id: string; name: string }[];
  /** Справочник типов вагонов организации (шаг 1); форма состава правит тип по нему. */
  carriageTypes?: RailCarriageTypeDto[];
}

/** Часы одного члена бригады в рейсе (шаг 3а). */
export interface OnboardCrewHours {
  membershipId: string;
  role: string;
  name: string;
  employeeNumber: string | null;
  /** С какого момента идёт счёт: начало приёмки или первое действие в рейсе. */
  from: string | null;
  /** По какой момент: сдача рейса, иначе конец рейса. */
  to: string | null;
  minutes: number;
  /** Те же минуты часами, с одним знаком: столбец «часы» маршрутного листа. */
  hours: number;
  /** Пусто — работал с начала рейса; иначе почему счёт начался позже. */
  note: string | null;
}

/** Итог рейса одним ответом (шаг 3а). */
export interface OnboardTripSummary {
  trip: {
    id: string;
    trainNumber: string;
    trainName: string | null;
    status: string;
    departure: string;
    arrival: string;
    plannedStart: string;
    plannedEnd: string;
    actualStart: string | null;
    actualEnd: string | null;
    handoverAt: string | null;
    handoverBy: { membershipId: string; name: string; role: string } | null;
    handoverUnlockMethod: string | null;
    /** Фактическое время в пути, минуты. Пусто — рейс ещё не закончен. */
    actualMinutes: number | null;
  };
  inspections: { planned: number; completed: number; missed: number };
  /** Осмотров вагонов: сохранённых чеклистов по всем обходам рейса. */
  carriageChecks: { done: number; carriages: number };
  defects: { total: number; open: number; closed: number };
  crew: OnboardCrewHours[];
}

const ONBOARD_LABELS: Record<string, string> = {
  planned: "Запланировано",
  active: "В рейсе",
  completed: "Завершено",
  cancelled: "Отменено",
  in_progress: "Выполняется",
  missed: "Пропущено",
  open: "Открыт",
  assigned: "Назначено",
  resolved: "Ремонт выполнен",
  verified: "Устранение проверено",
  closed: "Закрыт",
  low: "Низкий",
  medium: "Средний",
  high: "Высокий",
  critical: "Критический",
  yes: "Разрешена",
  no: "Запрещена",
  requires_decision: "Требуется решение руководителя",
  without_work_order: "Руководитель: наряд не требуется",
  work_order_required: "Требуется наряд-допуск",
  ok: "Исправно",
  warning: "Замечание",
  defect: "Неисправность",
  not_checked: "Не проверено",
  local: "На телефоне",
  pending: "Ожидает отправки",
  synced: "Синхронизировано",
  conflict: "Конфликт — черновик сохранён",
  created: "Создано",
  status: "Изменение статуса",
  saved: "Checklist сохранён",
  "interval-planned": "Следующий обход запланирован",
  "work-order-linked": "Наряд связан",
  "permit-decision": "Решение о наряде",
  "operation-decision": "Решение об эксплуатации",
  acceptance: "Приёмка",
  periodic: "Обход",
  handover: "Сдача",
};
export const onboardLabel = (value: string) => ONBOARD_LABELS[value] ?? value;

/* ───────── Учёт рабочего времени бортмехаников (шаг 6) ─────────
 *
 * Единица счёта — МИНУТА. Часы человеку показываются с одним знаком, а
 * складываются минуты: сложение округлённых часов не сходится с итогом месяца,
 * и табель начинает спорить сам с собой.
 */

/** Настройки учёта времени у организации. */
export interface OnboardSettings {
  /** За сколько минут до отправления явка по графику. */
  reportLeadMinutes: number;
  /** Месячная норма при суммированном учёте, минуты. */
  monthlyNormMinutes: number;
  /** Сколько списывает из копилки один день отгула, минуты. */
  dayOffMinutes: number;
}

export const SaveOnboardSettingsSchema = z.object({
  reportLeadMinutes: z.number().int().min(0).max(24 * 60),
  monthlyNormMinutes: z.number().int().min(0).max(31 * 24 * 60),
  dayOffMinutes: z.number().int().min(0).max(24 * 60),
});

/** Месяц учёта, ГГГГ-ММ: табель и итог работника считаются по месяцу. */
export const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Месяц — в виде ГГГГ-ММ");

/**
 * Маневровые работы ПО ФАКТУ. Мастер назначает: кто, когда, примерное начало —
 * окончание не называет. Работник сам нажимает «Начал» и «Закончил», время
 * ставит сервер.
 */
export const ShuntingWorkSchema = z.object({
  membershipId: id,
  /** Примерное начало; от него считается день работы, пока она не начата. */
  plannedAt: z.string().datetime(),
  note: text.optional(),
});

/** «Начал»: на назначенной работе — по её id, без id — своя отметка. */
export const ShuntingStartSchema = z.object({
  id: id.optional(),
  note: text.optional(),
});

/**
 * «Закончил». Работник времени не передаёт — его ставит сервер. Мастер,
 * закрывая работу за работника, обязан назвать время окончания.
 */
export const ShuntingFinishSchema = z.object({
  endedAt: z.string().datetime().optional(),
});

export const ShuntingStatusSchema = z.object({
  status: z.enum(["confirmed", "rejected"]),
});

export type ShuntingStatus = "pending" | "confirmed" | "rejected";

/** Где работа: назначена и не начата, идёт, закончена. */
export type ShuntingState = "assigned" | "in_progress" | "finished";

export interface OnboardShuntingWork {
  id: string;
  membershipId: string;
  name: string;
  employeeNumber: string | null;
  /** Примерное начало, назначенное мастером; у отметки работника пусто. */
  plannedAt: string | null;
  /** Фактическое начало; пусто — работа назначена, но не начата. */
  startedAt: string | null;
  /** Фактическое окончание; пусто — работа идёт (или не начата). */
  endedAt: string | null;
  /** Минуты от начала до окончания; пока работа не закончена — 0. */
  minutes: number;
  hours: number;
  note: string | null;
  state: ShuntingState;
  /** Подтверждение мастером: отметка самого работника ждёт его. */
  status: ShuntingStatus;
  /** Идёт дольше смены и не закрыта: мастеру — «не закрыто», работнику — напоминание. */
  notClosed: boolean;
  /** Отметил сам работник (а не назначил мастер). */
  selfMarked: boolean;
  createdByMembershipId: string;
  createdByName: string;
  /** Кто закрыл работу за работника, если он не закончил сам. */
  closedByName: string | null;
  confirmedAt: string | null;
}

/** Списание из копилки переработки. */
export const OvertimeOperationSchema = z
  .object({
    membershipId: id,
    kind: z.enum(["paid", "time_off"]),
    /** Часы оплаты; у отгула не передаются — списывается день по настройкам. */
    minutes: z.number().int().min(1).max(100 * 60).optional(),
    /** Дата отгула, ГГГГ-ММ-ДД; у оплаты — месяц, за который платят. */
    onDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Дата — в виде ГГГГ-ММ-ДД"),
    note: text.optional(),
  })
  .refine((x) => x.kind === "time_off" || !!x.minutes, "Укажите, сколько часов оплачено");

export interface OnboardOvertimeOperation {
  id: string;
  kind: "paid" | "time_off";
  minutes: number;
  hours: number;
  onDate: string;
  note: string | null;
  createdByName: string;
  createdAt: string;
}

/** Копилка переработки работника: накоплено, списано, остаток. */
export interface OnboardOvertimeBank {
  /** Накоплено за всё время: сумма переработок по месяцам. */
  accruedMinutes: number;
  paidMinutes: number;
  timeOffMinutes: number;
  /** Остаток: накоплено минус оплаченное и отгулянное. Может быть отрицательным. */
  balanceMinutes: number;
}

/** Строка «Моих рейсов»: рейс или маневровые работы одного дня. */
export interface OnboardWorkEntry {
  kind: "trip" | "shunting";
  /** Рейс — его id; маневровые — id записи. */
  id: string;
  date: string;
  /** «Рейс 001» или «Маневровые работы». */
  title: string;
  /** Маршрут у рейса; примечание у маневровых. */
  subtitle: string;
  from: string | null;
  to: string | null;
  minutes: number;
  hours: number;
  /** Маневровые работы, ждущие подтверждения, в часы месяца не вошли. */
  pending: boolean;
  /** Работа идёт: начата, не закончена. Часов у неё пока нет. */
  open?: boolean;
  /** Идёт дольше смены и не закрыта. */
  notClosed?: boolean;
}

/** Итог месяца у работника — экран «Мои рейсы». */
export interface OnboardWorkMonth {
  month: string;
  membershipId: string;
  name: string;
  employeeNumber: string | null;
  entries: OnboardWorkEntry[];
  factMinutes: number;
  /** Маневровые работы на подтверждении: в факт не вошли. */
  pendingMinutes: number;
  normMinutes: number;
  overtimeMinutes: number;
  bank: OnboardOvertimeBank;
  operations: OnboardOvertimeOperation[];
}

/** Табель за месяц: по каждому бортмеханику часы по дням. */
export interface OnboardTimesheetRow {
  membershipId: string;
  name: string;
  employeeNumber: string | null;
  /** Минуты по дням месяца: ключ — число (1…31). Пустой день не хранится. */
  days: Record<string, number>;
  factMinutes: number;
  pendingMinutes: number;
  normMinutes: number;
  overtimeMinutes: number;
  paidMinutes: number;
  timeOffMinutes: number;
  balanceMinutes: number;
}

export interface OnboardTimesheet {
  month: string;
  /** Сколько дней в месяце — по ним рисуются столбцы. */
  days: number;
  normMinutes: number;
  rows: OnboardTimesheetRow[];
}

// ─── Статистика руководителя (шаг 5) ────────────────────────────────────

/** Вагон в строке списка статистики; по строке открывается рейс, вагон или дефект. */
export interface OnboardStatsCarRef {
  tripId: string;
  trainNumber: string;
  carriageId: string;
  carriageNumber: string;
  typeCode: string | null;
}

/** Осмотр вагона в раскрытой строке группы: вагон, рейс, дата, секунды. */
export type OnboardStatsVisit = OnboardStatsCarRef & {
  inspectionId: string;
  inspector: string;
  at: string;
  /** Секунды осмотра вагона; пусто — время закрытия телефон не прислал. */
  seconds: number | null;
};

/**
 * Строка списка «по людям» или «по вагонам»: сколько осмотров попало в список и
 * какую долю это составляет от ВСЕХ его осмотров, у которых время известно.
 */
export interface OnboardStatsGroup {
  /** Имя работника или идентификатор вагона. */
  key: string;
  /** ФИО либо «№ вагона · тип». */
  label: string;
  count: number;
  total: number;
  /** count / total, 0…1. Список отсортирован по нему. */
  share: number;
  items: OnboardStatsVisit[];
}

/** Один список в двух разрезах: по людям (по умолчанию) и по вагонам. */
export interface OnboardStatsGroups {
  byPerson: OnboardStatsGroup[];
  byCar: OnboardStatsGroup[];
}

/** Статистика периода по рейсам, которые начинаются в этом периоде. */
export interface OnboardStats {
  period: { from: string; to: string };
  /** Порог «короткого осмотра» в секундах — настройка организации. */
  quickSeconds: number;
  tiles: {
    trips: number;
    roundsDone: number;
    roundsPlanned: number;
    roundsMissed: number;
    defects: number;
    /** Открыто сейчас — по всем рейсам, а не только по периоду. */
    openNow: number;
  };
  /** Дефекты по системам: Электрооборудование, ДГУ, Ходовая и тормоза, Интерьер (прочее — отдельной строкой). */
  systems: { name: string; count: number }[];
  /** Вагоны с повторными дефектами за период, по убыванию. */
  repeatCars: (OnboardStatsCarRef & { count: number; defectId: string; inspectionId: string })[];
  timing: {
    acceptanceAvgSeconds: number | null;
    acceptanceCount: number;
    roundAvgSeconds: number | null;
    roundCount: number;
    /** Среднее время на вагон по типу вагона. */
    carsByType: { typeCode: string; avgSeconds: number; count: number }[];
    /** Осмотров вагона с замеренным временем. */
    measured: number;
    /** «Короткие» осмотры — просто список, без выводов. */
    quick: (OnboardStatsCarRef & { inspectionId: string; seconds: number; inspector: string; at: string })[];
    /** Те же короткие осмотры по людям и по вагонам; доля — от всех осмотров с замеренным временем. */
    quickGroups: OnboardStatsGroups;
  };
  /** Осмотры без скана QR: вагон открыт нажатием на схему. */
  noScan: {
    /** Осмотров вагона, у которых телефон прислал время открытия. */
    total: number;
    count: number;
    share: number | null;
    items: (OnboardStatsCarRef & { inspectionId: string; inspector: string; at: string })[];
    /** Те же осмотры по людям и по вагонам; доля — от всех осмотров, где телефон прислал время открытия. */
    groups: OnboardStatsGroups;
  };
}

/** Ссылка на выгрузку Excel: короткоживущая, открывается в браузере телефона. */
export interface OnboardStatsExportLink {
  path: string;
  fileName: string;
}

// ─── Доступы, должности и роли (сборка bem-preview) ─────────────────────
//
// Роли — это профили прав организации (существующий механизм); здесь только то,
// как их называть человеку. Права профилей — в `capability-registry.ts` сервера.

/** Роли, которые руководитель назначает на экране «Сотрудники». */
export const STAFF_ASSIGNABLE_PROFILES = [
  "ONBOARD_CREW",
  "WORK_PRODUCER",
  "ADMITTING_PERSON",
  "RESPONSIBLE_MANAGER",
  "WORK_ORDER_ISSUER",
] as const;
export type StaffAssignableProfile = (typeof STAFF_ASSIGNABLE_PROFILES)[number];

export const StaffRoleChangeSchema = z.object({
  profile: z.enum(STAFF_ASSIGNABLE_PROFILES),
  action: z.enum(["assign", "revoke"]),
});

/** Название роли в нарядах и в рейсах: по профилям человека, без технических слов. */
const WORK_ORDER_ROLE: Partial<Record<string, string>> = {
  WORK_ORDER_ISSUER: "выдающий наряд",
  ADMITTING_PERSON: "допускающий",
  RESPONSIBLE_MANAGER: "ответственный руководитель",
  WORK_PRODUCER: "производитель работ",
  ONBOARD_CREW: "член бригады",
};
const TRIP_ROLE: Partial<Record<string, string>> = {
  ONBOARD_CREW: "бортмеханик",
  WORK_PRODUCER: "ведёт график, маневровые и табель",
  UNIT_MANAGER: "ведёт график и табель, назначает роли",
  RESPONSIBLE_MANAGER: "смотрит график, ведёт дефекты",
  WORK_ORDER_ISSUER: "смотрит график и статистику",
  HSE_SPECIALIST: "смотрит статистику, ведёт допуски",
};
const roleOrder = (labels: Partial<Record<string, string>>, profiles: readonly string[]) => [
  ...new Set(Object.keys(labels).filter((p) => profiles.includes(p)).map((p) => labels[p] as string)),
];

/** «Мои роли»: в нарядах и в рейсах. Технический профиль «работник» не показывается. */
export function bemRolesOf(profiles: readonly string[]): { workOrders: string[]; trips: string[] } {
  return { workOrders: roleOrder(WORK_ORDER_ROLE, profiles), trips: roleOrder(TRIP_ROLE, profiles) };
}

/** Выдающие наряд: подсказка тем, кто выдавать не может. */
export interface OnboardIssuer {
  name: string;
  position: string | null;
}

/** Профиль человека в сборке: должность, подразделение, роли. */
export interface OnboardMe {
  name: string;
  employeeNumber: string | null;
  position: string | null;
  subdivision: string | null;
  roles: { workOrders: string[]; trips: string[] };
  issuers: OnboardIssuer[];
}

/** Строка экрана «Сотрудники». */
export interface OnboardStaffMember {
  membershipId: string;
  name: string;
  employeeNumber: string | null;
  position: string | null;
  subdivision: string | null;
  /** Профессия бортмеханика: только такому выдаётся роль бортмеханика. */
  isCrew: boolean;
  roles: { workOrders: string[]; trips: string[] };
  /** Назначенные роли, которыми управляет руководитель (для кнопок «назначить/снять»). */
  assigned: StaffAssignableProfile[];
  /** Допуск на сегодня; у не бортмеханика — пусто. */
  admission: CrewAdmission | null;
  /** Занятость сегодня: рейс или свободен; у не бортмеханика — пусто. */
  busy: { trainNumber: string; role: string } | null;
  /** Часы за текущий месяц; видны тем, кто видит табель. */
  monthHours: number | null;
}

export interface OnboardStaff {
  members: OnboardStaffMember[];
  /** Может назначать и снимать роли. */
  canManage: boolean;
}

/** Дефект организации для экрана «Дефекты» тех, кто ведёт и смотрит дефекты. */
export interface OnboardDefectRow {
  id: string;
  tripId: string;
  trainNumber: string;
  carriageNumber: string;
  typeCode: string | null;
  system: string;
  equipment: string;
  description: string;
  priority: string;
  status: string;
  operationAllowed: string;
  createdAt: string;
  foundBy: string;
}
export interface OnboardDefectOverview {
  rows: OnboardDefectRow[];
  /** Может менять статус и решение по дефекту. */
  canManage: boolean;
}

// ─── Демо-учётки DEMO-BEM ───────────────────────────────────────────────
//
// Один источник на сид (должности и ФИО) и на экран входа сборки bem-preview
// (раскрывающийся список «Демо-учётки»). Пароль здесь не хранится и на экране
// не подставляется. Имена вымышленные.
export interface BemDemoAccount {
  employeeNumber: string;
  /** Роль входа: у бортмехаников — WORKER, у остальных — SENIOR. */
  loginRole: "WORKER" | "SENIOR";
  name: string;
  position: string;
}

export const BEM_DEMO_ACCOUNTS: readonly BemDemoAccount[] = [
  { employeeNumber: "930007", loginRole: "SENIOR", name: "Нургалиев Марат Бауыржанович", position: "Руководитель" },
  { employeeNumber: "930005", loginRole: "SENIOR", name: "Омаров Данияр Асхатович", position: "Мастер участка" },
  { employeeNumber: "930001", loginRole: "SENIOR", name: "Искаков Тимур Русланович", position: "Мастер участка" },
  { employeeNumber: "930004", loginRole: "SENIOR", name: "Жумабеков Нурлан Серикович", position: "Начальник цеха" },
  { employeeNumber: "930003", loginRole: "SENIOR", name: "Сарсенов Ерлан Кайратович", position: "Начальник ПТО" },
  { employeeNumber: "930006", loginRole: "SENIOR", name: "Абдрахманова Гульнара Талгатовна", position: "Инженер по охране труда" },
  { employeeNumber: "930002", loginRole: "WORKER", name: "Бекмуратов Айдар Саматович", position: "Бортовой электромеханик" },
  { employeeNumber: "930008", loginRole: "WORKER", name: "Ахметов Руслан Маратович", position: "Бортовой электромеханик" },
  { employeeNumber: "930009", loginRole: "WORKER", name: "Касымов Арман Болатович", position: "Бортовой электромеханик" },
  { employeeNumber: "930010", loginRole: "WORKER", name: "Ким Виктор Сергеевич", position: "Бортовой электромеханик" },
  { employeeNumber: "930011", loginRole: "WORKER", name: "Нуртаев Бауыржан Ермекович", position: "Бортовой электромеханик" },
  { employeeNumber: "930012", loginRole: "WORKER", name: "Жумабек Ерасыл Талгатович", position: "Бортовой электромеханик" },
  { employeeNumber: "930013", loginRole: "WORKER", name: "Тулеев Аскар Нурланович", position: "Бортовой электромеханик" },
  // Сторонняя организация (ТОО «Сұңқар»): заправщик WG — журнал открывает только вкладку «WG».
  { employeeNumber: "930020", loginRole: "WORKER", name: "Сейткали Нурбол Ержанович", position: "Заправщик WG, ТОО «Сұңқар»" },
];

// ─── Матрица доступа БЭМ: что есть в меню и на экранах ──────────────────
//
// Чего человеку нельзя — того нет в его меню и на его экранах. Меню и кнопки
// собираются из прав (capabilities) этой функцией, сервер проверяет те же права.
// Одна функция на приложение и на тесты: ожидания по каждой демо-учётке пишутся
// в тесте сервера литералами и сверяются с тем, что построит приложение.
export interface BemAccess {
  /** Осмотры и сдача своего рейса (бортмеханик). */
  tripRun: boolean;
  /** Смотрит рейсы по графику — открывает рейс из карточки (мастер, руководитель). */
  tripView: boolean;
  scheduleRead: boolean;
  scheduleManage: boolean;
  shuntingOwn: boolean;
  shuntingRead: boolean;
  shuntingManage: boolean;
  timesheetRead: boolean;
  timesheetManage: boolean;
  statsRead: boolean;
  statsManage: boolean;
  defectRead: boolean;
  defectManage: boolean;
  staffRead: boolean;
  staffManage: boolean;
  /** Журнал дежурства: смотрит / ведёт / справочник, инвентарь и Excel. */
  dutyRead: boolean;
  dutyWrite: boolean;
  dutyManage: boolean;
  /** Топливо WG: смотреть WG и историю / записать заправку (заправщик ТОО «Сұңқар» — только это). */
  fuelRead: boolean;
  fuelRecord: boolean;
  workOrdersRead: boolean;
  issueWorkOrder: boolean;
}

export function bemAccessOf(capabilities: readonly string[]): BemAccess {
  const has = (c: string) => capabilities.includes(c);
  return {
    tripRun: has("ONBOARD_TRIP_RUN"),
    tripView: has("ONBOARD_SCHEDULE_MANAGE"),
    scheduleRead: has("ONBOARD_SCHEDULE_READ"),
    scheduleManage: has("ONBOARD_SCHEDULE_MANAGE"),
    shuntingOwn: has("ONBOARD_SHUNTING_OWN"),
    shuntingRead: has("ONBOARD_SHUNTING_READ") || has("ONBOARD_SHUNTING_MANAGE"),
    shuntingManage: has("ONBOARD_SHUNTING_MANAGE"),
    timesheetRead: has("ONBOARD_TIMESHEET_READ") || has("ONBOARD_TIMESHEET_MANAGE"),
    timesheetManage: has("ONBOARD_TIMESHEET_MANAGE"),
    statsRead: has("ONBOARD_STATS_READ") || has("ONBOARD_STATS_MANAGE"),
    statsManage: has("ONBOARD_STATS_MANAGE"),
    defectRead: has("ONBOARD_DEFECT_READ") || has("ONBOARD_DEFECT_MANAGE"),
    defectManage: has("ONBOARD_DEFECT_MANAGE"),
    staffRead: has("ONBOARD_STAFF_READ") || has("ONBOARD_STAFF_MANAGE"),
    staffManage: has("ONBOARD_STAFF_MANAGE"),
    dutyRead: has("ONBOARD_DUTY_READ") || has("ONBOARD_DUTY_WRITE") || has("ONBOARD_DUTY_MANAGE"),
    dutyWrite: has("ONBOARD_DUTY_WRITE"),
    dutyManage: has("ONBOARD_DUTY_MANAGE"),
    fuelRead: has("ONBOARD_FUEL_READ") || has("ONBOARD_FUEL_RECORD"),
    fuelRecord: has("ONBOARD_FUEL_RECORD"),
    workOrdersRead: has("WORK_ORDER_READ"),
    issueWorkOrder: has("WORK_ORDER_ISSUE"),
  };
}

export type BemTab =
  | "onboard"
  | "onboard-schedule"
  | "onboard-stats"
  | "onboard-defects"
  | "work-orders"
  | "onboard-staff";

/** Нижнее меню: первая вкладка — та, что открывается после входа. */
export function bemTabsOf(a: BemAccess): BemTab[] {
  const tabs: BemTab[] = [];
  if (a.tripRun) tabs.push("onboard");
  else if (a.scheduleRead) tabs.push("onboard-schedule");
  else if (a.statsRead) tabs.push("onboard-stats");
  if (a.defectRead || a.tripRun) tabs.push("onboard-defects");
  if (a.workOrdersRead) tabs.push("work-orders");
  if (a.staffRead) tabs.push("onboard-staff");
  return tabs.length ? tabs : ["work-orders"];
}

/** Кнопки в шапке графика (экран «График»): каждая — только при праве на свой экран. */
export function bemScheduleShortcuts(a: BemAccess): ("fleet" | "qr" | "stats" | "timesheet" | "shunting")[] {
  const out: ("fleet" | "qr" | "stats" | "timesheet" | "shunting")[] = [];
  // Кнопки живут в шапке графика; без графика (охрана труда) у статистики своя вкладка.
  if (!a.scheduleRead) return out;
  out.push("fleet", "qr");
  if (a.statsRead) out.push("stats");
  if (a.timesheetRead) out.push("timesheet");
  if (a.shuntingRead) out.push("shunting");
  return out;
}

// ─── Журнал дежурства бортмехаников ──────────────────────────────────────────
//
// Замечание привязано к ВАГОНУ (номеру), а не к месту стоянки: техпарк и путь у
// замечания — где вагон стоял в момент выявления. Состав переставили — история
// вагона читается по номеру и не теряется.

/** old — СТПП (старый техпарк), new — НТПП (новый). */
export const DUTY_YARDS = ["old", "new"] as const;
export const DUTY_CAR_TYPES = ["WS", "WSb", "WL", "WG"] as const;
export const REMARK_STATUSES = ["open", "in_progress", "resolved"] as const;
export const DUTY_SHIFTS = ["day", "night"] as const;
export const SHIFT_EVENT_KINDS = ["wg_start", "wg_stop", "shunting", "long_storage", "power_switch", "other", "fuel_report"] as const;
/** События, которые записывают кнопками; «показания отправлены» пишется само, при отправке. */
export const MANUAL_EVENT_KINDS = SHIFT_EVENT_KINDS.filter((k) => k !== "fuel_report");
export const WG_STATES = ["running", "stopped"] as const;
export const REMARK_MAX_PHOTOS = 6;
export const REMARK_PHOTO_MAX_BASE64 = 700_000;
/** Файл справочника (CSV или Excel) в base64: около 2 МБ. */
export const CAR_IMPORT_MAX_BASE64 = 2_800_000;
export type DutyYard = (typeof DUTY_YARDS)[number];
export type DutyCarType = (typeof DUTY_CAR_TYPES)[number];
export type RemarkStatus = (typeof REMARK_STATUSES)[number];
export type DutyShiftKind = (typeof DUTY_SHIFTS)[number];
export type ShiftEventKind = (typeof SHIFT_EVENT_KINDS)[number];
export type WgState = (typeof WG_STATES)[number];

const DUTY_LABELS = {
  old: "СТПП",
  new: "НТПП",
  open: "Открыто",
  in_progress: "В работе",
  resolved: "Устранено",
  day: "День",
  night: "Ночь",
  running: "Работает",
  stopped: "Заглушен",
  wg_start: "Пуск WG",
  wg_stop: "Останов WG",
  shunting: "Манёвры",
  long_storage: "Перевод на длительный отстой",
  power_switch: "Переключение питания",
  other: "Другое",
  fuel_report: "Показания отправлены Сұңқар",
} as const;
export type DutyLabelKey = keyof typeof DUTY_LABELS;
export const dutyLabel = (v: DutyLabelKey) => DUTY_LABELS[v];
export const yardFullName = (y: DutyYard) => (y === "old" ? "Старый техпарк (СТПП)" : "Новый техпарк (НТПП)");

/** Номер вагона — 5 цифр; заводской номер — N-NNN. */
export const CAR_NUMBER_RE = /^\d{5}$/;
export const FACTORY_NUMBER_RE = /^\d-\d{3}$/;
/**
 * Номер в одном виде: без пробелов и без префикса таблички. На вагоне номер
 * пишется полностью — «068/18553»; ищут и ссылаются по пяти цифрам после
 * косой черты. «50 101» = «50101», «068/18553» = «18553».
 */
export function normalizeCarNumber(value: string): string {
  const v = value.replace(/\s+/g, "").toUpperCase();
  const slash = v.lastIndexOf("/");
  return slash >= 0 ? v.slice(slash + 1) : v;
}

/**
 * Номер, как его ввели: «068/18553» → номер 18553 и полный номер с таблички
 * «068/18553»; «18553» → только номер. Полный номер хранится, если введён.
 */
export function parseCarInput(value: string): { carNumber: string; fullNumber: string | null } {
  const v = value.replace(/\s+/g, "").toUpperCase();
  const carNumber = normalizeCarNumber(v);
  return { carNumber, fullNumber: v.includes("/") && v.slice(0, v.lastIndexOf("/")) ? v : null };
}
/** Заводской номер в одном виде: «1 101», «1101», «1–101» → «1-101». */
export function normalizeFactoryNumber(value: string): string {
  const v = value.replace(/\s+/g, "").replace(/[‐-―−]/g, "-");
  return /^\d{4}$/.test(v) ? `${v[0]}-${v.slice(1)}` : v;
}

const key = z.string().uuid();
const carRef = z
  .string()
  .transform(normalizeCarNumber)
  .pipe(z.string().min(1, "Укажите вагон").max(32, "Слишком длинный номер вагона"));
const track = z.number().int("Путь — целое число").min(1, "Путь — от 1").max(99, "Путь — до 99");
const percent = (what: string) => z.number().int(`${what} — целое число`).min(0, `${what}: от 0 до 100`).max(100, `${what}: от 0 до 100`);
const isoTime = z.string().datetime();
const shiftDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Дата смены: ГГГГ-ММ-ДД");

// Справочник вагонов
export const AddDutyCarSchema = z.object({
  /** Пять цифр или полный номер с таблички «068/18553» — тогда он хранится. */
  carNumber: z
    .string()
    .refine((v) => CAR_NUMBER_RE.test(normalizeCarNumber(v)), "Номер вагона — 5 цифр (можно полностью: 068/18553)")
    .refine((v) => (parseCarInput(v).fullNumber ?? "").length <= 32, "Слишком длинный номер вагона"),
  factoryNumber: z
    .string()
    .transform(normalizeFactoryNumber)
    .pipe(z.string().regex(FACTORY_NUMBER_RE, "Заводской номер — в виде 1-234"))
    .optional(),
  /** Не указан — определяется по серии номера (правила серий организации). */
  carType: z.enum(DUTY_CAR_TYPES).optional(),
  yard: z.enum(DUTY_YARDS).optional(),
  track: track.optional(),
});
export type AddDutyCar = z.infer<typeof AddDutyCarSchema>;
export const CarImportSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentBase64: z.string().min(1).max(CAR_IMPORT_MAX_BASE64, "Файл слишком большой"),
});
export interface CarImportResult {
  added: number;
  updated: number;
  skipped: { row: number; message: string }[];
  /** Сохранено, но номер, тип или заводской номер не сходятся с серией — проверить. */
  warnings: { row: number; carNumber: string; message: string }[];
}
export interface AddDutyCarResult {
  car: DutyCar;
  /** Сохранено, но стоит проверить номер или тип. */
  warnings: string[];
}

// Правила серий: начало номера → тип и первая цифра заводского номера.
export const SeriesRuleSchema = z.object({
  id: z.string().uuid().optional(),
  prefix: z.string().trim().regex(/^\d{1,4}$/, "Начало номера — от 1 до 4 цифр"),
  carType: z.enum(DUTY_CAR_TYPES),
  /** Первая цифра заводского номера; пусто — не проверяется. */
  factoryDigit: z.string().regex(/^\d$/, "Первая цифра заводского номера — одна цифра").nullable().optional(),
  note: z.string().trim().max(200).nullable().optional(),
  active: z.boolean().default(true),
});
export const SaveSeriesRulesSchema = z.object({
  rules: z
    .array(SeriesRuleSchema)
    .max(100)
    .refine((a) => new Set(a.map((r) => r.prefix)).size === a.length, "Начало номера повторяется"),
});
export interface SeriesRule {
  id: string;
  prefix: string;
  carType: DutyCarType;
  factoryDigit: string | null;
  note: string | null;
  active: boolean;
}

export interface SeriesCheck {
  /** Тип по серии; null — ни одно правило не подошло. */
  seriesType: DutyCarType | null;
  /** Начало номера, по которому сработало правило. */
  prefix: string | null;
  /** Предупреждения «проверьте номер/тип»: сохранять можно. */
  warnings: string[];
}

/**
 * Проверка вагона по правилам серий. Срабатывает самое длинное подходящее
 * начало номера (правило «181» сильнее правила «18»). Выключенные правила не
 * участвуют. Ничего не запрещает — только предупреждает.
 */
export function checkCarSeries(
  rules: readonly Pick<SeriesRule, "prefix" | "carType" | "factoryDigit" | "active">[],
  input: { carNumber: string; factoryNumber?: string | null; carType?: DutyCarType | null },
): SeriesCheck {
  const n = normalizeCarNumber(input.carNumber);
  const rule = rules
    .filter((r) => r.active && n.startsWith(r.prefix))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0];
  const warnings: string[] = [];
  if (!rule) {
    warnings.push(`Номер ${n} не подходит ни под одну серию — проверьте номер и тип`);
    return { seriesType: null, prefix: null, warnings };
  }
  if (input.carType && input.carType !== rule.carType) {
    warnings.push(`Серия ${rule.prefix}xxx — это ${rule.carType}, а выбран ${input.carType}. Проверьте номер/тип`);
  }
  const f = input.factoryNumber ? normalizeFactoryNumber(input.factoryNumber) : "";
  if (f && rule.factoryDigit && f[0] !== rule.factoryDigit) {
    warnings.push(`Заводской номер ${f} не совпадает с серией ${rule.prefix}xxx (ожидается ${rule.factoryDigit}-xxx). Проверьте номер`);
  }
  return { seriesType: rule.carType, prefix: rule.prefix, warnings };
}

// Дислокация
export const MoveCarsSchema = z.object({
  requestKey: key,
  carNumbers: z.array(carRef).min(1, "Выберите вагоны").max(300),
  toYard: z.enum(DUTY_YARDS),
  toTrack: track,
  movedAt: isoTime.optional(),
});
export type MoveCars = z.infer<typeof MoveCarsSchema>;

/**
 * Расстановка пути: номера по порядку от начала пути. Заменяет состав пути
 * целиком — вагоны с других путей переставляются сюда, вагоны этого пути,
 * которых нет в списке, остаются без места. Пустой список — путь свободен.
 */
export const ArrangeTrackSchema = z.object({
  requestKey: key,
  yard: z.enum(DUTY_YARDS),
  track,
  carNumbers: z
    .array(carRef)
    .max(300)
    .refine((a) => new Set(a).size === a.length, "Вагон в списке повторяется"),
  arrangedAt: isoTime.optional(),
});
export type ArrangeTrack = z.infer<typeof ArrangeTrackSchema>;

/** Названия концов пути: откуда начинается счёт вагонов и где путь кончается. */
const trackEnd = z
  .string()
  .trim()
  .max(60, "Название конца пути — до 60 знаков")
  .nullable()
  .optional()
  .transform((v) => (v ? v : null));
export const SaveDutyTracksSchema = z.object({
  tracks: z
    .array(z.object({ yard: z.enum(DUTY_YARDS), track, startName: trackEnd, endName: trackEnd }))
    .max(200)
    .refine((a) => new Set(a.map((t) => `${t.yard}:${t.track}`)).size === a.length, "Путь повторяется"),
});
export interface DutyTrackView {
  yard: DutyYard;
  track: number;
  /** Начало пути — от него считаются вагоны («Нурлы жол»). */
  startName: string | null;
  /** Конец пути («упор»). */
  endName: string | null;
}

// Замечания
export const CreateRemarkSchema = z.object({
  requestKey: key,
  carNumber: carRef,
  description: z.string().trim().min(1, "Опишите замечание").max(2000),
  /** Когда выявлено — по часам телефона (он мог быть без сети). */
  detectedAt: isoTime.optional(),
});
export type CreateRemark = z.infer<typeof CreateRemarkSchema>;
export const RemarkPhotoUploadSchema = z.object({
  requestKey: key,
  mimeType: z.enum(["image/jpeg", "image/png"]),
  contentBase64: z.string().min(1).max(REMARK_PHOTO_MAX_BASE64),
});
export type RemarkPhotoUpload = z.infer<typeof RemarkPhotoUploadSchema>;
export const RemarkStatusChangeSchema = z.object({
  requestKey: key,
  status: z.enum(REMARK_STATUSES),
  comment: z.string().trim().max(2000).optional(),
});
export type RemarkStatusChange = z.infer<typeof RemarkStatusChangeSchema>;
export const RemarkCommentSchema = z.object({
  requestKey: key,
  text: z.string().trim().min(1, "Напишите комментарий").max(2000),
});
export type RemarkComment = z.infer<typeof RemarkCommentSchema>;

// Обход WG: все WG одной кнопкой
export const WgReadingSchema = z.object({
  carNumber: carRef,
  fuel1: percent("Топливо, бак 1"),
  fuel2: percent("Топливо, бак 2").optional(),
  water: percent("Уровень воды").optional(),
  state: z.enum(WG_STATES),
  startedAt: isoTime.optional(),
  stoppedAt: isoTime.optional(),
  comment: z.string().trim().max(1000).optional(),
});
export type WgReadingInput = z.infer<typeof WgReadingSchema>;
export const WgRoundSchema = z.object({
  requestKey: key,
  recordedAt: isoTime.optional(),
  readings: z
    .array(WgReadingSchema)
    .min(1, "Заполните хотя бы один WG")
    .max(100)
    .refine((a) => new Set(a.map((r) => r.carNumber)).size === a.length, "WG повторяется"),
});
export type WgRound = z.infer<typeof WgRoundSchema>;

// Заправка WG
/** Кто заправляет WG по умолчанию — сторонняя организация. */
export const FUEL_SUPPLIER_NAME = "Сұңқар";
export const WgRefuelSchema = z
  .object({
    requestKey: key,
    carNumber: carRef,
    before1: percent("Было, бак 1").optional(),
    before2: percent("Было, бак 2").optional(),
    after1: percent("Стало, бак 1"),
    after2: percent("Стало, бак 2").optional(),
    byName: z.string().trim().min(1, "Кто заправил").max(120).default(FUEL_SUPPLIER_NAME),
    at: isoTime,
    comment: z.string().trim().max(1000).optional(),
  })
  .refine((r) => r.before1 === undefined || r.after1 >= r.before1, { message: "Стало меньше, чем было — проверьте бак 1", path: ["after1"] });
export type WgRefuelInput = z.infer<typeof WgRefuelSchema>;

// События смены
export const ShiftEventSchema = z
  .object({
    requestKey: key,
    at: isoTime,
    carNumber: carRef.optional(),
    kind: z.enum(SHIFT_EVENT_KINDS),
    text: z.string().trim().max(2000).optional(),
  })
  .refine((e) => e.kind !== "other" || !!e.text, { message: "Опишите, что сделано", path: ["text"] });
export type ShiftEventInput = z.infer<typeof ShiftEventSchema>;

// Инвентарь
export const SaveInventorySchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        name: z.string().trim().min(1, "Название позиции").max(120),
        active: z.boolean().default(true),
      }),
    )
    .max(100)
    .refine((a) => new Set(a.map((i) => i.name.toLowerCase())).size === a.length, "Позиция повторяется"),
});
const inventoryCounts = z
  .array(z.object({ itemId: z.string().uuid(), qty: z.number().int().min(0).max(999) }))
  .max(100);

// Смена
export const StartShiftSchema = z.object({
  requestKey: key,
  shiftDate,
  shift: z.enum(DUTY_SHIFTS),
  temperature: z.number().int().min(-60).max(60).optional(),
  dutyMembershipIds: z.array(z.string().uuid()).min(1, "Укажите дежурных").max(10),
});
export type StartShift = z.infer<typeof StartShiftSchema>;
export const UpdateShiftSchema = z.object({
  temperature: z.number().int().min(-60).max(60).nullable().optional(),
  dutyMembershipIds: z.array(z.string().uuid()).min(1, "Укажите дежурных").max(10).optional(),
});
export const HandOverShiftSchema = z.object({
  requestKey: key,
  toMembershipId: z.string().uuid(),
  inventory: inventoryCounts,
});
export type HandOverShift = z.infer<typeof HandOverShiftSchema>;
export const AcceptShiftSchema = z
  .object({
    requestKey: key,
    result: z.enum(["ok", "with_remarks"]),
    note: z.string().trim().max(2000).optional(),
    inventory: inventoryCounts,
  })
  .refine((a) => a.result === "ok" || !!a.note, { message: "Опишите расхождение", path: ["note"] });
export type AcceptShift = z.infer<typeof AcceptShiftSchema>;

// Что отдаёт сервер
export interface DutyCar {
  id: string;
  carNumber: string;
  /** Полный номер с таблички («068/18553»), если его вводили. */
  fullNumber: string | null;
  factoryNumber: string | null;
  carType: DutyCarType | null;
  yard: DutyYard | null;
  track: number | null;
  /** Место на пути от начала (1 — первый от начала); null — порядок не задан. */
  position: number | null;
  locatedAt: string | null;
  openRemarks: number;
}
export interface RemarkView {
  id: string;
  carNumber: string;
  factoryNumber: string | null;
  carType: DutyCarType | null;
  yard: DutyYard | null;
  track: number | null;
  description: string;
  status: RemarkStatus;
  reportedByName: string;
  detectedAt: string;
  /** За кем числится сейчас: тот, кто выявил, а после приёма смены — принявший. */
  responsibleName: string;
  responsibleMembershipId: string;
  resolvedByName: string | null;
  resolvedAt: string | null;
  comment: string | null;
  photoIds: string[];
}
export interface RemarkEventView {
  id: string;
  kind: "created" | "status" | "comment" | "handover";
  fromStatus: RemarkStatus | null;
  toStatus: RemarkStatus | null;
  note: string | null;
  actorName: string;
  at: string;
}
export interface RemarkDetail extends RemarkView {
  events: RemarkEventView[];
}
export interface MoveView {
  id: string;
  carNumber: string;
  fromYard: DutyYard | null;
  fromTrack: number | null;
  /** null — вагон снят с пути при расстановке и остался без места. */
  toYard: DutyYard | null;
  toTrack: number | null;
  byName: string;
  at: string;
}
export interface WgReadingView {
  carNumber: string;
  fuel1: number;
  fuel2: number | null;
  water: number | null;
  state: WgState;
  startedAt: string | null;
  stoppedAt: string | null;
  comment: string | null;
  byName: string;
  at: string;
}
export interface WgRefuelView {
  id: string;
  carNumber: string;
  before1: number | null;
  before2: number | null;
  after1: number;
  after2: number | null;
  byName: string;
  comment: string | null;
  recordedByName: string;
  at: string;
}
/** История топлива и воды одной WG: обходы и заправки. */
export interface WgFuelHistory {
  car: DutyCar;
  readings: WgReadingView[];
  refuels: WgRefuelView[];
}
export interface WgStatus {
  car: DutyCar;
  /** Последняя запись обхода; null — WG ещё не обходили. */
  last: WgReadingView | null;
}
export interface ShiftEventView {
  id: string;
  at: string;
  carNumber: string | null;
  kind: ShiftEventKind;
  text: string | null;
  byName: string;
}
export interface InventoryItemView {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
}
export interface InventoryCount {
  itemId: string;
  name: string;
  qty: number;
}
export interface DutyColleague {
  membershipId: string;
  name: string;
  employeeNumber: string;
}
/** Сводка смены: что сдаётся. У сданной смены — снимок на момент сдачи. */
export interface ShiftSummary {
  openRemarks: RemarkView[];
  wg: WgStatus[];
  dislocation: DutyCar[];
}
export interface ShiftView {
  id: string;
  shiftDate: string;
  shift: DutyShiftKind;
  temperature: number | null;
  dutyMembershipIds: string[];
  dutyNames: string[];
  status: "open" | "handed" | "accepted";
  openedAt: string;
  handedByName: string | null;
  toMembershipId: string | null;
  toName: string | null;
  handedAt: string | null;
  acceptedAt: string | null;
  acceptResult: "ok" | "with_remarks" | null;
  acceptNote: string | null;
}
export interface ShiftDetail extends ShiftView {
  /** У идущей смены — живая сводка, у сданной — снимок на момент сдачи. */
  summary: ShiftSummary;
  handoverInventory: InventoryCount[] | null;
  acceptInventory: InventoryCount[] | null;
  events: ShiftEventView[];
  moves: MoveView[];
  wgReadings: WgReadingView[];
  refuels: WgRefuelView[];
}
export interface CarCard {
  car: DutyCar;
  remarks: RemarkView[];
  events: ShiftEventView[];
  moves: MoveView[];
  wgReadings: WgReadingView[];
}

/** Переходы статуса: открыто → в работе → устранено; устранённое можно вернуть в открытое. */
export function remarkTransitionAllowed(from: RemarkStatus, to: RemarkStatus): boolean {
  if (from === to) return false;
  if (from === "resolved") return to === "open";
  return true;
}
export const isRemarkOpen = (status: RemarkStatus) => status !== "resolved";

/** Следующая смена после этой: день → ночь того же дня, ночь → день следующего. */
export function nextShiftOf(shiftDate: string, shift: DutyShiftKind): { shiftDate: string; shift: DutyShiftKind } {
  if (shift === "day") return { shiftDate, shift: "night" };
  const d = new Date(`${shiftDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return { shiftDate: d.toISOString().slice(0, 10), shift: "day" };
}

/** Подсказка вагонов по первым цифрам номера (и заводскому номеру). */
export function suggestCars<T extends { carNumber: string; factoryNumber: string | null }>(
  cars: readonly T[],
  query: string,
  limit = 8,
): T[] {
  const q = normalizeCarNumber(query);
  if (!q) return [];
  const starts = cars.filter((c) => c.carNumber.startsWith(q));
  const rest = cars.filter((c) => !c.carNumber.startsWith(q) && (c.carNumber.includes(q) || (c.factoryNumber ?? "").replace("-", "").includes(q.replace("-", ""))));
  return [...starts, ...rest].slice(0, limit);
}

// ─── Табель дежурств ────────────────────────────────────────────────────────
//
// Мастер загружает месячный табель из Excel: строки — работники, столбцы —
// дни, в ячейках коды. Коды дежурства в техпарке (Х1–Х4) говорят, кто и где
// дежурит; прочие (8, НМ, О, В…) хранятся для справки. Что значит код —
// таблица кодов организации, её правит мастер. Кто дежурит сейчас и кто
// следующий, считается из табеля на телефоне — работает и без сети.

export interface DutyCodeView {
  code: string;
  /** Дежурство в техпарке; false — код для справки (отпуск, выходной, учёба…). */
  duty: boolean;
  yard: DutyYard | null;
  /** «08:00»; у дежурства — начало и конец, у прочих — пусто. */
  start: string | null;
  end: string | null;
  note: string | null;
}

/** Таблица кодов по умолчанию: заводится организации, пока мастер её не поменял. */
export const DEFAULT_DUTY_CODES: readonly DutyCodeView[] = [
  { code: "Х1", duty: true, yard: "new", start: "08:00", end: "20:00", note: "День, НТПП" },
  { code: "Х2", duty: true, yard: "old", start: "08:00", end: "20:00", note: "День, СТПП" },
  { code: "Х3", duty: true, yard: "new", start: "20:00", end: "08:00", note: "Ночь, НТПП" },
  { code: "Х4", duty: true, yard: "old", start: "20:00", end: "08:00", note: "Ночь, СТПП" },
  ...(
    [
      ["8", "Рабочий день 8 ч"],
      ["НМ", null],
      ["ДМ", null],
      ["НМВ", null],
      ["К", "Командировка"],
      ["О", "Отпуск"],
      ["В", "Выходной"],
      ["D", null],
      ["Э", null],
      ["Б", "Больничный"],
      ["БС", "Без сохранения"],
      ["У", "Учёба"],
      ["АТ", "Аттестация"],
    ] as const
  ).map(([code, note]) => ({ code, duty: false, yard: null, start: null, end: null, note })),
];

const TIME = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Время — ЧЧ:ММ");
export const SaveDutyCodesSchema = z.object({
  codes: z
    .array(
      z
        .object({
          code: z.string().transform((v) => normalizeDutyCode(v)).pipe(z.string().min(1, "Укажите код").max(8, "Код — до 8 знаков")),
          duty: z.boolean(),
          yard: z.enum(DUTY_YARDS).nullable().optional(),
          start: TIME.nullable().optional(),
          end: TIME.nullable().optional(),
          note: z.string().trim().max(120).nullable().optional(),
        })
        .refine((c) => !c.duty || (c.yard && c.start && c.end), { message: "У дежурства укажите техпарк, начало и конец", path: ["yard"] }),
    )
    .max(100)
    .refine((a) => new Set(a.map((c) => c.code)).size === a.length, "Код повторяется"),
});

// Латинские буквы, похожие на русские: в Excel «X1» часто набирают латиницей.
const LATIN_TO_CYRILLIC: Record<string, string> = { A: "А", B: "В", C: "С", E: "Е", H: "Н", K: "К", M: "М", O: "О", P: "Р", T: "Т", X: "Х", Y: "У" };

/** Код табеля в одном виде: «x 1», «X1», «х1» → «Х1»; «d» → «D» (латинская D — свой код). */
export function normalizeDutyCode(raw: string): string {
  const v = raw.replace(/\s+/g, "").toUpperCase();
  if (v === "D") return v;
  return v.replace(/[ABCEHKMOPTXY]/g, (ch) => LATIN_TO_CYRILLIC[ch] ?? ch);
}

/** Строка табеля: человек и его коды по дням месяца («1» … «31»). */
export interface DutyRosterRow {
  membershipId: string | null;
  name: string;
  codes: Record<string, string>;
}
export interface DutyRosterView {
  month: string;
  rows: DutyRosterRow[];
  codes: DutyCodeView[];
}
const monthRe = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Месяц: ГГГГ-ММ");
export const RosterPreviewSchema = z.object({
  month: monthRe,
  fileName: z.string().trim().min(1).max(255),
  contentBase64: z.string().min(1).max(CAR_IMPORT_MAX_BASE64, "Файл слишком большой"),
});
export const SaveRosterSchema = z.object({
  month: monthRe,
  rows: z
    .array(
      z.object({
        membershipId: z.string().uuid().nullable(),
        name: z.string().trim().min(1).max(200),
        codes: z.record(z.string().regex(/^([1-9]|[12]\d|3[01])$/), z.string().max(8)),
      }),
    )
    .max(300)
    .refine((a) => new Set(a.filter((r) => r.membershipId).map((r) => r.membershipId)).size === a.filter((r) => r.membershipId).length, "Работник встречается в табеле дважды"),
});
export interface RosterPreviewRow extends DutyRosterRow {
  /** Строка в файле. */
  row: number;
  employeeNumber: string | null;
  /** Как узнан: по табельному номеру, по ФИО или по фамилии и инициалам; null — не узнан. */
  matchedBy: "number" | "name" | "initials" | null;
}
export interface RosterPreview {
  month: string;
  rows: RosterPreviewRow[];
  /** Коды, которых нет в таблице кодов: сохранить можно, но они ничего не значат. */
  unknownCodes: string[];
  problems: string[];
}

/** Половина суток дежурства: день (с утра) или ночь (с вечера). */
export type DutyHalf = DutyShiftKind;
export interface DutySlot {
  shiftDate: string;
  shift: DutyHalf;
}

/** Что значит код: дежурство в техпарке на дневную или ночную половину; null — не дежурство. */
export function dutyOfCode(codes: readonly DutyCodeView[], raw: string | undefined): { yard: DutyYard; shift: DutyHalf; code: string } | null {
  if (!raw) return null;
  const code = normalizeDutyCode(raw);
  const c = codes.find((x) => x.code === code);
  if (!c || !c.duty || !c.yard || !c.start) return null;
  const hour = Number(c.start.slice(0, 2));
  return { yard: c.yard, shift: hour >= 6 && hour < 18 ? "day" : "night", code };
}

/** Код человека на дату: из строки табеля нужного месяца. */
function codeOn(rows: readonly (DutyRosterRow & { month: string })[], membershipId: string, date: string): string | undefined {
  const month = date.slice(0, 7);
  const day = String(Number(date.slice(8, 10)));
  return rows.find((r) => r.month === month && r.membershipId === membershipId)?.codes[day];
}

export interface OnDuty {
  membershipId: string;
  name: string;
  code: string;
}

/** Кто дежурит в половине суток — по техпаркам. Табель может состоять из нескольких месяцев. */
export function dutyAt(
  rows: readonly (DutyRosterRow & { month: string })[],
  codes: readonly DutyCodeView[],
  slot: DutySlot,
): Record<DutyYard, OnDuty[]> {
  const out: Record<DutyYard, OnDuty[]> = { old: [], new: [] };
  const month = slot.shiftDate.slice(0, 7);
  const day = String(Number(slot.shiftDate.slice(8, 10)));
  for (const r of rows) {
    if (r.month !== month || !r.membershipId) continue;
    const d = dutyOfCode(codes, r.codes[day]);
    if (d && d.shift === slot.shift) out[d.yard].push({ membershipId: r.membershipId, name: r.name, code: d.code });
  }
  return out;
}

/**
 * Кому передавать смену: следующая смена того же техпарка. Х2 (день СТПП) →
 * Х4 (ночь СТПП) того же дня, Х4 → Х2 следующего дня; так же Х1 ↔ Х3 на
 * НТПП. Техпарк берётся из кода сдающего на эту смену; нет кода — null.
 */
export function suggestReceiver(
  rows: readonly (DutyRosterRow & { month: string })[],
  codes: readonly DutyCodeView[],
  membershipId: string,
  slot: DutySlot,
): { yard: DutyYard; receiver: OnDuty | null; next: DutySlot } | null {
  const mine = dutyOfCode(codes, codeOn(rows, membershipId, slot.shiftDate));
  if (!mine || mine.shift !== slot.shift) return null;
  const next = nextShiftOf(slot.shiftDate, slot.shift);
  const candidates = dutyAt(rows, codes, next)[mine.yard].filter((p) => p.membershipId !== membershipId);
  return { yard: mine.yard, receiver: candidates[0] ?? null, next };
}

/** Какая половина суток идёт сейчас: с 08:00 до 20:00 — день, иначе ночь (после полуночи — ночь вчерашней даты). */
export function slotAt(now: Date): DutySlot {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  const date = local.toISOString().slice(0, 10);
  const hour = now.getHours();
  if (hour >= 8 && hour < 20) return { shiftDate: date, shift: "day" };
  if (hour >= 20) return { shiftDate: date, shift: "night" };
  const prev = new Date(`${date}T00:00:00Z`);
  prev.setUTCDate(prev.getUTCDate() - 1);
  return { shiftDate: prev.toISOString().slice(0, 10), shift: "night" };
}
